/**
 * Centralizovana definicija svih backend API endpoint-ova.
 * Rute su organizovane po feature modulima i koriste se zajedno sa ApiService.
 */

/** Bazni path za sve API rute. */
const API_BASE = '/ogpbe';

/**
 * Mapa svih API endpoint-ova organizovana po modulima.
 * @type {object}
 */
export const API_ENDPOINTS = {

  // ==================== AUTHENTICATION ====================
  AUTH: {
    USER: `${API_BASE}/auth/user`,
    LOGOUT: `${API_BASE}/auth/logout`,
  },

  // ==================== ACCOUNT ====================
  ACCOUNT: {
    PROFILE_INFO_GET: `${API_BASE}/profile-info`,
    LOGIN: `${API_BASE}/login`,
    PROFILE_INFO_SAVE_OR_UPDATE: `${API_BASE}/profile-info/save-or-update`,
    SEARCH: `${API_BASE}/account/search`,
    SEARCH_BY_ORGANIZATION: `${API_BASE}/account/search-by-organization`,
  },

  // ==================== ACCOUNT IN ORGANIZATION ====================
  ACCOUNT_ORGANIZATION: {
    /** Admin direktno dodaje člana (BE: `accounts/organizations/add`, ne `grant-access`). */
    GRANT_ACCESS: `${API_BASE}/accounts/organizations/add`,
    VERIFY_ACCESS: `${API_BASE}/accounts/organizations/verify-access`,
    REQUEST_ACCESS: `${API_BASE}/accounts/organizations/request-access`,
    REJECT_ACCESS: `${API_BASE}/accounts/organizations/reject-access`,
    PROMOTE_TO_LEAD: `${API_BASE}/accounts/organizations/promote-to-lead`,
    DEMOTE_FROM_LEAD: `${API_BASE}/accounts/organizations/demote-from-lead`,
    INVALIDATE_ACCESS: `${API_BASE}/accounts/organizations/invalidate-access`,
    ORGANIZATION_VERIFIED: `${API_BASE}/accounts/organization-verified`,
    ORGANIZATIONS_VERIFIED: `${API_BASE}/accounts/organizations/verified`,
    ORGANIZATIONS: `${API_BASE}/accounts/organizations`,
    ORGANIZATION_VERIFICATION: `${API_BASE}/accounts/organization/verification`,
  },

  // ==================== OGP (Open Government Partnership) ====================
  OGP: {
    // Nacrti akcionih planova (Action Plan Drafts)
    DRAFTS: {
      LIST: `${API_BASE}/ogp/drafts`,
      DETAILS: (id) => `${API_BASE}/ogp/drafts/${id}`,
      PDF: (id) => `${API_BASE}/ogp/drafts/${id}/pdf`,
      DOWNLOAD: (id) => `${API_BASE}/ogp/drafts/${id}/download`,
    },

    // Komentari na nacrtima
    COMMENTS: {
      LIST: (draftId) => `${API_BASE}/ogp/drafts/${draftId}/comments`,
      CREATE: (draftId) => `${API_BASE}/ogp/drafts/${draftId}/comments`,
      UPDATE: (draftId, commentId) => `${API_BASE}/ogp/drafts/${draftId}/comments/${commentId}`,
      DELETE: (draftId, commentId) => `${API_BASE}/ogp/drafts/${draftId}/comments/${commentId}`,
      VOTE: (commentId) => `${API_BASE}/ogp/comments/${commentId}/vote`,
    },

    // Izveštaji (Reports)
    REPORTS: {
      LIST: `${API_BASE}/ogp/reports`,
      DETAILS: (id) => `${API_BASE}/ogp/reports/${id}`,
      PDF: (id) => `${API_BASE}/ogp/reports/${id}/pdf`,
      DOWNLOAD: (id) => `${API_BASE}/ogp/reports/${id}/download`,
    },

    // Korisna dokumenta
    DOCUMENTS: {
      LIST: `${API_BASE}/ogp/documents`,
      DOWNLOAD: (id) => `${API_BASE}/ogp/documents/${id}/download`,
    },

    // Obaveštenja (Notices/Announcements)
    NOTICES: {
      LIST: `${API_BASE}/ogp/notices`,
      DETAILS: (id) => `${API_BASE}/ogp/notices/${id}`,
    },

    // OGP Administrativno
    ADMIN: {
      FIELDS_OF_INTEREST: `${API_BASE}/ogp/admin/fields-of-interest`,
      MODERATORS: `${API_BASE}/ogp/admin/moderators`,
    },

    // Akcioni planovi (Action Plans)
    ACTION_PLAN: {
      SEARCH: `${API_BASE}/action-plan/search`,
      SEARCH_V2: `${API_BASE}/action-plan/search-v2`,
      STATUS_CHANGE: `${API_BASE}/action-plan/status/change`,
      STATUS_HISTORY: `${API_BASE}/action-plan/status/history`,
      SAVE_OR_UPDATE: `${API_BASE}/action-plan/save-or-update`,
    },

    // Obaveze (Obligations)
    OBLIGATION: {
      SEARCH: `${API_BASE}/obligation/search`,
      ACTIVITY_SEARCH: `${API_BASE}/obligation/activity/search`,
      /** Predlozi aktivnosti (usaglašavanje): POST search i save-or-update. */
      PROPOSAL_ACTIVITY_SAVE_OR_UPDATE: `${API_BASE}/obligation/activity/proposal/save-or-update`,
      PROPOSAL_ACTIVITY_SEARCH: `${API_BASE}/obligation/activity/proposal/search`,
      ACTIVITY_SAVE_OR_UPDATE: `${API_BASE}/obligation/activity/save-or-update`,
      ACTIVITY_ARCHIVE_SEARCH: `${API_BASE}/obligation/activity/archive/search`,
      /** Redosled aktivnosti obaveze: { obligation_id, activity_ids }. */
      ACTIVITY_REORDER: `${API_BASE}/obligation/activity/reorder`,
      /** Redosled obaveza akcionog plana: { action_plan_id, obligation_ids }. */
      REORDER: `${API_BASE}/obligation/reorder`,
      PROPOSAL_SEARCH: `${API_BASE}/obligation/proposal/search`,
      PROPOSAL_SEARCH_ONE: `${API_BASE}/obligation/proposal/search-one`,
      PROPOSAL_ARCHIVE_SEARCH: `${API_BASE}/obligation/proposal/archive/search`,
      PROPOSAL_STATUS_UPDATE: `${API_BASE}/obligation/proposal/status-update`,
      PROPOSAL_SAVE_OR_UPDATE: `${API_BASE}/obligation/proposal/save-or-update`,
      SAVE_OR_UPDATE: `${API_BASE}/obligation/save-or-update`,
      STATUS_UPDATE: `${API_BASE}/obligation/status/update`,
      RELATED_ORGANIZATION_SEARCH: `${API_BASE}/obligation/related-organization/search`,
      RELATED_ORGANIZATION_ADD: `${API_BASE}/obligation/related-organization/add`,
      RELATED_ORGANIZATION_REMOVE: `${API_BASE}/obligation/related-organization/remove`,
      MODERATOR_ADD: `${API_BASE}/obligation/moderator/add`,
      MODERATOR_REMOVE: `${API_BASE}/obligation/moderator/remove`,
      MODERATOR_ACCEPT: `${API_BASE}/obligation/moderator/accept-moderation`,
      MODERATOR_REJECT: `${API_BASE}/obligation/moderator/reject-moderation`,
      IMPLEMENTATION_SEARCH: `${API_BASE}/obligation/implementation/search`,
      IMPLEMENTATION_SAVE_OR_UPDATE: `${API_BASE}/obligation/implementation/save-or-update`,
      DRAFT_SAVE_OR_UPDATE: `${API_BASE}/obligation/draft/save-or-update`,
      DRAFT_SEARCH: `${API_BASE}/obligation/draft/search`,
      DRAFT_SUBMIT: `${API_BASE}/obligation/draft/submit`,
    },
  },

  // ==================== USER MANAGEMENT ====================
  USER: {
    PROFILE: {
      GET: `${API_BASE}/user/profile`,
      UPDATE: `${API_BASE}/user/profile`,
      PREFERENCES: `${API_BASE}/user/preferences`,
      ACTIVITY: `${API_BASE}/user/activity`,
      /** GDPR — pravo na zaborav. */
      FORGET_ME: `${API_BASE}/user/forget-me`,
    },
    ROLES: `${API_BASE}/user/roles`,
  },

  // ==================== CSO (Civil Society Organizations) ====================
  CSO: {
    LIST: `${API_BASE}/cso`,
    SEARCH: `${API_BASE}/cso/search`,
    CREATE: `${API_BASE}/cso`,
    DETAILS: (id) => `${API_BASE}/cso/${id}`,
    UPDATE: (id) => `${API_BASE}/cso/${id}`,
    VERIFY: (id) => `${API_BASE}/cso/${id}/verify`,
    REJECT: (id) => `${API_BASE}/cso/${id}/reject`,
    DRAFT: (id) => `${API_BASE}/cso/${id}/draft`,
    SUBMIT: (id) => `${API_BASE}/cso/${id}/submit`,
    BY_USER: (userId) => `${API_BASE}/cso/by-user/${userId}`,
    VERIFIED: `${API_BASE}/cso/verified`,
    PENDING: `${API_BASE}/cso/pending`,
    /** Novi endpoint-i prema specifikaciji. */
    ADD: `${API_BASE}/cso/add`,
    /** Građanska prijava OCD (org + team lead + kontakt u jednom pozivu). */
    REGISTER_BY_USER: `${API_BASE}/cso/register`,
    APPROVE: `${API_BASE}/cso/approve`,
    REJECT_NEW: `${API_BASE}/cso/reject`,
    INVALIDATE: `${API_BASE}/cso/invalidate`,
    APPROVAL_LIST: `${API_BASE}/cso/approval`,
    // CSO Registri
    REGISTER: {
      LIST: `${API_BASE}/cso/cso-register`,
      ADD: `${API_BASE}/cso/cso-register/add`,
      INVALIDATE: `${API_BASE}/cso/cso-register/invalidate`,
    },
  },

  // ==================== INSTITUTIONS ====================
  INSTITUTIONS: {
    LIST: `${API_BASE}/institutions`,
    DETAILS: (id) => `${API_BASE}/institutions/${id}`,
  },

  // ==================== ORGANIZATION (additional info – contact person) ====================
  ORGANIZATION: {
    ADDITIONAL_INFO_SEARCH: `${API_BASE}/organization/additional-info-search`,
    SAVE_ADDITIONAL_INFO: `${API_BASE}/organization/save-additional-info`,
  },

  // ==================== FIELDS OF INTEREST ====================
  FIELDS_OF_INTEREST: {
    LIST: `${API_BASE}/fields-of-interest`,
    DETAILS: (id) => `${API_BASE}/fields-of-interest/${id}`,
  },

  // ==================== FILE UPLOAD ====================
  FILES: {
    UPLOAD: `${API_BASE}/files/upload`,
    DOWNLOAD: (id) => `${API_BASE}/files/${id}/download`,
    DELETE: (id) => `${API_BASE}/files/${id}`,
  },

  // ==================== DOCUMENT API ====================
  DOCUMENT: {
    UPLOAD: `${API_BASE}/document/upload`,
    SEARCH: `${API_BASE}/document/search`,
    DOWNLOAD: `${API_BASE}/document/download`,
    DELETE: `${API_BASE}/document/delete`,
    TOGGLE_COMMENT: `${API_BASE}/document/toggle-comment`,
    COMMENT_SEARCH: `${API_BASE}/document/comment/search`,
    COMMENT_SAVE_OR_UPDATE: `${API_BASE}/document/comment/save-or-update`,
    COMMENT_STATUS_CHANGE: `${API_BASE}/document/comment/status/change`,
  },

  // ==================== NOTICE API ====================
  NOTICE: {
    SEARCH: `${API_BASE}/notice/search`,
    SEARCH_ONE: `${API_BASE}/notice/search-one`,
    SEARCH_BY_DOCUMENT: `${API_BASE}/notice/search-by-document`,
    SEARCH_ARCHIVED: `${API_BASE}/notice/search-archived`,
    SAVE_OR_UPDATE: `${API_BASE}/notice/save-or-update`,
    INVALIDATE: `${API_BASE}/notice/invalidate`,
  },

  // ==================== SEO API ====================
  SEO: {
    SEARCH_ONE: `${API_BASE}/seo/search-one`,
    SAVE_OR_UPDATE: `${API_BASE}/seo/save-or-update`,
  },

  // ==================== DICTIONARY API ====================
  DICTIONARY: {
    SEARCH: `${API_BASE}/notice/dictionary/search`,
    SAVE_OR_UPDATE: `${API_BASE}/notice/dictionary/save-or-update`,
  },

  // ==================== PUBLIC BODY ====================
  PUBLIC_BODY: {
    LIST: `${API_BASE}/public-body`,
    ADD: `${API_BASE}/public-body/add`,
    /** Građanska prijava institucije (org + team lead + kontakt u jednom pozivu). */
    REGISTER_BY_USER: `${API_BASE}/public-body/register`,
    UPDATE: (id) => `${API_BASE}/public-body/${id}`,
    APPROVE: `${API_BASE}/public-body/approve`,
    REJECT: `${API_BASE}/public-body/reject`,
    INVALIDATE: `${API_BASE}/public-body/invalidate`,
    APPROVAL_LIST: `${API_BASE}/public-body/approval`,
    REGISTER: {
      LIST: `${API_BASE}/public-body/public-body-register`,
      ADD: `${API_BASE}/public-body/public-body-register/add`,
      INVALIDATE: `${API_BASE}/public-body/public-body-register/invalidate`,
    },
  },

  // ==================== ADMIN PANEL ====================
  ADMIN: {
    USERS: {
      LIST: `${API_BASE}/admin/users`,
      DETAILS: (id) => `${API_BASE}/admin/users/${id}`,
      UPDATE_ROLE: (id) => `${API_BASE}/admin/users/${id}/role`,
      LINK_CSO: (userId, csoId) => `${API_BASE}/admin/users/${userId}/link-cso/${csoId}`,
      LINK_INSTITUTION: (userId, institutionId) => `${API_BASE}/admin/users/${userId}/link-institution/${institutionId}`,
    },
    WORKSPACE: {
      STATS: `${API_BASE}/admin/workspace/stats`,
      RELATIONSHIPS: (entityType, entityId) => `${API_BASE}/admin/workspace/relationships/${entityType}/${entityId}`,
      SEARCH: `${API_BASE}/admin/workspace/search`,
    },
    ERROR_LOGS: `${API_BASE}/admin/error-logs`,
    MAIL_SETTINGS: `${API_BASE}/admin/mail-settings`,
  },

  // ==================== ADMIN DASHBOARD ====================
  DASHBOARD: {
    OVERVIEW_SEARCH: `${API_BASE}/dashboard/overview/search`,
  },

  // ==================== NOTIFICATION API ====================
  NOTIFICATION: {
    SEARCH: `${API_BASE}/notification/search`,
    READ_TOGGLE: `${API_BASE}/notification/read-toggle`,
  },
};

/**
 * Kreira URL query string iz objekta parametara.
 * Preskače `null` i `undefined` vrednosti.
 *
 * @param {Object} params - Objekat sa query parametrima.
 * @returns {string} Query string (npr. `?page=1&size=10`) ili prazan string.
 *
 * @example
 * buildQueryString({ page: 1, size: 10 }) // '?page=1&size=10'
 */
export const buildQueryString = (params) => {
  const query = Object.keys(params)
    .filter(key => params[key] !== null && params[key] !== undefined)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');

  return query ? `?${query}` : '';
};

export default API_ENDPOINTS;

